import { AllProductsArrivalDetailsType } from '@src/quince-site-common/src/services/zipCodeService/zipCodeService.types'
import { ProductIdVsVariantIdMap } from './ProductArrivalMessage'
import { DeliveryDateRange, DeliveryDetailsType } from '@src/quince-site-common/src/domains/ProductDeliveryDetails'

const getMinDate = (date1?: DeliveryDateRange, date2?: DeliveryDateRange) => {
  try {
    if (date1 && date2) {
      if (Date.parse(date1.maximum) < Date.parse(date2.maximum)) {
        return date1
      }
      return date2
    }
    return date1 || date2
  } catch (error) {
    console.error('Error calculating minimum date for product delivery date range')
    return undefined
  }
}

const getMaxDate = (date1?: DeliveryDateRange, date2?: DeliveryDateRange) => {
  try {
    if (date1 && date2) {
      if (Date.parse(date1.maximum) < Date.parse(date2.maximum)) {
        return date2
      }
      return date1
    }
    return date1 || date2
  } catch (error) {
    console.error('Error calculating maximum date for product delivery date range')
    return undefined
  }
}

function getProductDeliveryInfoFromSelectedVariantDetails(selectedVariantsArrivalDetails: DeliveryDetailsType[]) {
  let expressDeliveryPossibleByCutoff = false
  let standardDeliveryPossibleByCutoff = false
  let expressDeliveryDate: DeliveryDateRange | undefined
  let standardDeliveryDate: DeliveryDateRange | undefined

  for (const variantArrivalDetails of selectedVariantsArrivalDetails) {
    const expressDeliveryPossibleForVariant = variantArrivalDetails.EXPRESS.is_delivered_by_date
    const standardDeliveryPossibleForVariant = variantArrivalDetails.STANDARD.is_delivered_by_date

    const expressDeliveryDateForVariant = variantArrivalDetails.EXPRESS.estimated_delivery_date
    const standardDeliveryDateForVariant = variantArrivalDetails.STANDARD.estimated_delivery_date

    expressDeliveryPossibleByCutoff = expressDeliveryPossibleByCutoff || expressDeliveryPossibleForVariant
    expressDeliveryDate = expressDeliveryPossibleForVariant
      ? getMinDate(expressDeliveryDate, expressDeliveryDateForVariant)
      : expressDeliveryDate

    standardDeliveryPossibleByCutoff = standardDeliveryPossibleByCutoff || standardDeliveryPossibleForVariant
    standardDeliveryDate = standardDeliveryPossibleForVariant
      ? getMinDate(standardDeliveryDate, standardDeliveryDateForVariant)
      : standardDeliveryDate
  }
  return {
    expressDeliveryPossibleByCutoff,
    standardDeliveryPossibleByCutoff,
    expressDeliveryDate,
    standardDeliveryDate,
  }
}

export function getDeliveryInformationForSelection(
  selectedProductIdsVsVariantIdsMap: ProductIdVsVariantIdMap,
  allProductsArrivalDetails: AllProductsArrivalDetailsType,
  zipCode: string | undefined,
) {
  if (!zipCode) return null

  // Delivery only possible for bundles if all products are deliverable by date
  let standardDeliveryPossibleForAllProducts = true
  let expressDeliveryPossibleForAllProducts = true

  let maxStandardDeliveryDateForAllProducts: DeliveryDateRange | undefined
  let maxExpressDeliveryDateForAllProducts: DeliveryDateRange | undefined

  Object.entries(selectedProductIdsVsVariantIdsMap).forEach(([productId, variantIds]) => {
    const productArrivalDetails = allProductsArrivalDetails[productId]
    if (!productArrivalDetails) {
      console.error(`Unable to find delivery details for product id ${productId}`)
      standardDeliveryPossibleForAllProducts = false
      expressDeliveryPossibleForAllProducts = false
      return null
    }
    const selectedVariantsArrivalDetails = variantIds.map(id => productArrivalDetails[id])

    // product level delivery possibility for selected variants according to user selection
    const {
      standardDeliveryPossibleByCutoff,
      expressDeliveryPossibleByCutoff,
      standardDeliveryDate,
      expressDeliveryDate,
    } = getProductDeliveryInfoFromSelectedVariantDetails(selectedVariantsArrivalDetails)

    maxStandardDeliveryDateForAllProducts = getMaxDate(maxStandardDeliveryDateForAllProducts, standardDeliveryDate)
    standardDeliveryPossibleForAllProducts = standardDeliveryPossibleForAllProducts && standardDeliveryPossibleByCutoff

    maxExpressDeliveryDateForAllProducts = getMaxDate(maxExpressDeliveryDateForAllProducts, expressDeliveryDate)
    expressDeliveryPossibleForAllProducts = expressDeliveryPossibleForAllProducts && expressDeliveryPossibleByCutoff
  })

  return {
    standardDeliveryPossible: standardDeliveryPossibleForAllProducts,
    expressDeliveryPossible: expressDeliveryPossibleForAllProducts,
    standardDeliveryDate: standardDeliveryPossibleForAllProducts ? maxStandardDeliveryDateForAllProducts : undefined,
    expressDeliveryDate: expressDeliveryPossibleForAllProducts ? maxExpressDeliveryDateForAllProducts : undefined,
  }
}
